import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useStartEmployeeImport } from "../../../../api/queries/employeeQueries";
import { dataMappingApi, saveFile } from "../../../../api/adminToolsApi";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "../../../../utils/apiError";
import { extractBatchIdFromMessage } from "./importHelpers";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];
const TEMPLATE_FILE = "Employee_Upload_Template.xlsx";

const validateFile = (file) => {
  if (!file) return "Choose a file to import.";
  const name = file.name.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext)))
    return "Only .xlsx and .csv files are supported.";
  if (file.size > MAX_FILE_SIZE) return "The file is larger than 10 MB.";
  return null;
};

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * State A: choose company + file and start the import.
 * Calls onStarted({ batchId, totalRows, fileName }) after the API accepts it (201).
 */
const ImportUploadForm = ({ companies, defaultCompanyId, onStarted }) => {
  const fileInputRef = useRef(null);
  const [companyId, setCompanyId] = useState(defaultCompanyId || "");
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null); // { message, runningBatchId? }
  const startImport = useStartEmployeeImport();
  const isSending = startImport.isPending;

  const pickFile = (picked) => {
    if (!picked) return;
    setFile(picked);
    const problem = validateFile(picked);
    setError(problem ? { message: problem } : null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSending) return;
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!isSending) pickFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const problem = !companyId
      ? "Select the company to import into."
      : validateFile(file);
    if (problem) {
      setError({ message: problem });
      return;
    }
    setError(null);
    startImport.mutate(
      { file, companyId },
      {
        onSuccess: (res) =>
          onStarted({
            batchId: res.batchId,
            totalRows: res.totalRows,
            fileName: file.name,
            companyId,
          }),
        onError: (err) => {
          const message = getApiErrorMessage(
            err,
            "Could not start the import.",
          );
          setError({
            message,
            runningBatchId:
              getApiErrorStatus(err) === 409
                ? extractBatchIdFromMessage(message)
                : null,
            isConflict: getApiErrorStatus(err) === 409,
          });
        },
      },
    );
  };

  const canSubmit = !!companyId && !!file && !validateFile(file) && !isSending;

  // The workbook built from the company's sheet/column setup; the bundled
  // file is the fallback (e.g. no Data tools access)
  const [downloading, setDownloading] = useState(false);
  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      saveFile(await dataMappingApi.templateFile(companyId || undefined));
    } catch {
      const a = document.createElement("a");
      a.href = `${process.env.PUBLIC_URL || ""}/${TEMPLATE_FILE}`;
      a.download = TEMPLATE_FILE;
      a.click();
      toast.info("Downloaded the standard template.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <form className="import-card" onSubmit={handleSubmit} noValidate>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-4">
        <div>
          <h5 className="fw-bold text-navy mb-1">New employees (workbook)</h5>
          <p className="text-slate-500 fs-085 mb-0">
            The workbook has a sheet for each part of the record (personal, job,
            family…). Fill it in, then upload it here. Big files keep running in
            the background, so you can leave this page.
          </p>
        </div>
      </div>

      <div className="mb-4">
        <span className="step-label">1. Get the workbook</span>
        <button
          type="button"
          className="btn-template d-inline-flex align-items-center"
          onClick={downloadTemplate}
          disabled={downloading}
        >
          <i className="bi bi-file-earmark-arrow-down me-1"></i>
          {downloading ? "Preparing…" : "Download the workbook template"}
        </button>
        <p className="text-slate-500 fs-075 mt-1 mb-0">
          Its sheets and columns match your company&apos;s{" "}
          <Link to="/admin/data-mapping">workbook columns</Link>. Keep the
          header rows as they are.
        </p>
      </div>

      <div className="mb-4">
        <label htmlFor="import-company" className="step-label">
          2. Company to add them to <span className="text-danger">*</span>
        </label>
        <select
          id="import-company"
          className="form-select-custom"
          value={companyId}
          onChange={(e) => setCompanyId(e.target.value)}
          disabled={isSending}
          required
        >
          <option value="">-- Select company --</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.companyName}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <span className="step-label">
          3. Your filled-in workbook <span className="text-danger">*</span>
        </span>
        <div
          role="button"
          tabIndex={0}
          aria-label="Choose a file to import"
          className={`upload-zone ${dragActive ? "active" : ""} ${isSending ? "disabled" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !isSending && fileInputRef.current?.click()}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") &&
            !isSending &&
            fileInputRef.current?.click()
          }
        >
          <input
            ref={fileInputRef}
            type="file"
            className="d-none"
            accept=".xlsx,.csv"
            onChange={(e) => {
              pickFile(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {!file ? (
            <>
              <i className="bi bi-cloud-arrow-up-fill text-primary mb-2 fs-200"></i>
              <p className="m-0 fw-bold small">Click or drag a file here</p>
              <p className="m-0 text-slate-500 fs-075">
                .xlsx or .csv, up to 10 MB
              </p>
            </>
          ) : (
            <div className="d-flex align-items-center justify-content-center gap-2">
              <i className="bi bi-file-earmark-excel-fill text-success fs-150"></i>
              <span className="fw-bold small text-truncate mw-250px">
                {file.name}
              </span>
              <span className="text-slate-500 fs-075">
                ({formatSize(file.size)})
              </span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div
          className={`alert ${error.isConflict ? "alert-warning" : "alert-danger"} fs-085`}
          role="alert"
        >
          <i
            className={`bi ${error.isConflict ? "bi-hourglass-split" : "bi-exclamation-triangle-fill"} me-2`}
          ></i>
          {error.message}
          {error.isConflict && (
            <div className="mt-2">
              {error.runningBatchId ? (
                <Link
                  to={`/employee/import/${error.runningBatchId}`}
                  className="fw-semibold"
                >
                  View its progress →
                </Link>
              ) : (
                "Please wait for it to finish, then try again."
              )}
            </div>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end">
        <button
          type="submit"
          className="btn btn-primary btn-main upload-btn px-4"
          disabled={!canSubmit}
        >
          {isSending ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                aria-hidden="true"
              ></span>
              Sending…
            </>
          ) : (
            <>
              <i className="bi bi-upload me-2"></i>Start import
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ImportUploadForm;
